import Layout from '@/components/layout/layout'
import CustomersEntity from '@/entities/api/customers/customer'

const ProductSkeleton = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	return (
		<Layout customer={customer} token={token}>
			<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse'>
				<div className='lg:grid lg:grid-cols-2 lg:gap-x-8 lg:items-start'>
					<div className='flex flex-col-reverse'>
						<div className='mx-auto mt-6 hidden w-full max-w-2xl sm:block lg:max-w-none'>
							<div className='grid grid-cols-4 gap-6'>
								{[...Array(4)].map((_, index) => (
									<div
										key={index}
										className='relative h-24 bg-gray-200 rounded-md border border-gray-300'
									></div>
								))}
							</div>
						</div>

						<div className='aspect-w-1 aspect-h-1 w-full relative'>
							<div className='w-full h-96 bg-gray-200 sm:rounded-lg'></div>
						</div>
					</div>

					<div className='mt-10 px-4 sm:px-0 sm:mt-16 lg:mt-0'>
						<div className='h-8 bg-gray-200 rounded w-3/4 mb-4'></div>

						<div className='mt-3'>
							<div className='flex items-center space-x-1'>
								{[...Array(5)].map((_, index) => (
									<div
										key={index}
										className='h-5 w-5 bg-gray-200 rounded'
									></div>
								))}
								<div className='h-4 bg-gray-200 rounded w-20 ml-2'></div>
							</div>
						</div>

						<div className='mt-6'>
							<div className='flex items-center'>
								<div className='h-10 bg-gray-200 rounded w-32'></div>
								<div className='ml-4 flex items-center space-x-2'>
									<div className='h-6 bg-gray-200 rounded w-24'></div>
									<div className='h-6 bg-gray-200 rounded-full w-16'></div>
								</div>
							</div>

							<div className='mt-2 flex items-center'>
								<div className='h-4 w-4 bg-gray-200 rounded mr-1'></div>
								<div className='h-4 bg-gray-200 rounded w-16'></div>
								<div className='h-4 bg-gray-200 rounded w-32 ml-2'></div>
							</div>
						</div>

						<div className='mt-6'>
							<div className='space-y-3'>
								<div className='h-4 bg-gray-200 rounded w-full'></div>
								<div className='h-4 bg-gray-200 rounded w-5/6'></div>
								<div className='h-4 bg-gray-200 rounded w-4/6'></div>
							</div>
						</div>

						<div className='mt-6 bg-gray-50 p-4 rounded-lg'>
							<div className='h-5 bg-gray-200 rounded w-32 mb-3'></div>
							<div className='space-y-2'>
								{[...Array(5)].map((_, index) => (
									<div key={index} className='flex'>
										<div className='h-4 bg-gray-200 rounded w-16 mr-2'></div>
										<div className='h-4 bg-gray-200 rounded w-24'></div>
									</div>
								))}
							</div>
						</div>

						<div className='mt-6'>
							<div className='mb-4'>
								<div className='h-4 bg-gray-200 rounded w-16 mb-2'></div>
								<div className='flex flex-wrap gap-2'>
									{[...Array(6)].map((_, index) => (
										<div
											key={index}
											className='w-8 h-8 bg-gray-200 rounded-full border-2 border-gray-300'
										></div>
									))}
								</div>
							</div>

							<div className='mb-4'>
								<div className='h-4 bg-gray-200 rounded w-12 mb-2'></div>
								<div className='flex flex-wrap gap-2'>
									{[...Array(4)].map((_, index) => (
										<div
											key={index}
											className='h-10 w-16 bg-gray-200 rounded border border-gray-300'
										></div>
									))}
								</div>
							</div>

							<div className='mb-4'>
								<div className='h-4 bg-gray-200 rounded w-14 mb-2'></div>
								<div className='flex flex-wrap gap-2'>
									{[...Array(5)].map((_, index) => (
										<div
											key={index}
											className='h-10 w-20 bg-gray-200 rounded border border-gray-300'
										></div>
									))}
								</div>
							</div>
						</div>

						<div className='mt-6'>
							<div className='flex items-center mb-4'>
								<div className='h-4 bg-gray-200 rounded w-16 mr-4'></div>
								<div className='flex items-center border-2 border-gray-300 rounded-lg'>
									<div className='p-3 bg-gray-200 rounded-l-lg w-12 h-12'></div>
									<div className='w-20 h-12 bg-gray-100'></div>
									<div className='p-3 bg-gray-200 rounded-r-lg w-12 h-12'></div>
								</div>
							</div>

							<div className='w-full h-14 bg-gray-200 rounded-lg'></div>
						</div>

						<div className='mt-6 space-y-4'>
							<div className='flex items-start p-4 border border-gray-200 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100'>
								<div className='h-6 w-6 bg-gray-200 rounded mt-1 mr-3'></div>
								<div className='flex-1'>
									<div className='h-4 bg-gray-200 rounded w-20 mb-1'></div>
									<div className='h-3 bg-gray-200 rounded w-24'></div>
								</div>
							</div>

							<div className='flex items-start p-4 border border-gray-200 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100'>
								<div className='h-6 w-6 bg-gray-200 rounded mt-1 mr-3'></div>
								<div className='flex-1'>
									<div className='h-4 bg-gray-200 rounded w-24 mb-1'></div>
									<div className='h-3 bg-gray-200 rounded w-20'></div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className='mt-8 border-t pt-8'>
					<div className='flex items-center justify-between mb-6'>
						<div>
							<div className='h-6 bg-gray-200 rounded w-40 mb-2'></div>
							<div className='flex items-center mt-2'>
								<div className='flex space-x-1 mr-2'>
									{[...Array(5)].map((_, index) => (
										<div
											key={index}
											className='h-5 w-5 bg-gray-200 rounded'
										></div>
									))}
								</div>
								<div className='h-5 bg-gray-200 rounded w-8 mr-2'></div>
								<div className='h-4 bg-gray-200 rounded w-20'></div>
							</div>
						</div>

						<div className='flex space-x-4'>
							<div className='h-8 bg-gray-200 rounded w-32'></div>
							<div className='h-8 bg-gray-200 rounded w-32'></div>
						</div>
					</div>

					<div className='space-y-4'>
						{[...Array(3)].map((_, index) => (
							<div key={index} className='bg-gray-50 rounded-lg p-4'>
								<div className='flex items-start justify-between mb-2'>
									<div className='flex items-center space-x-3'>
										<div className='h-4 bg-gray-200 rounded w-32'></div>
										<div className='flex space-x-1'>
											{[...Array(5)].map((_, starIndex) => (
												<div
													key={starIndex}
													className='h-4 w-4 bg-gray-200 rounded'
												></div>
											))}
										</div>
									</div>
									<div className='h-3 bg-gray-200 rounded w-20'></div>
								</div>
								<div className='space-y-2'>
									<div className='h-4 bg-gray-200 rounded w-full'></div>
									<div className='h-4 bg-gray-200 rounded w-4/5'></div>
									<div className='h-4 bg-gray-200 rounded w-3/5'></div>
								</div>
							</div>
						))}
					</div>
				</div>

				<div className='mt-16'>
					<div className='h-8 bg-gray-200 rounded w-64 mb-6'></div>
					<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
						{[...Array(4)].map((_, index) => (
							<div key={index} className='group'>
								<div className='aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-200 mb-4'>
									<div className='w-full h-full bg-gray-200'></div>
								</div>

								<div className='space-y-2'>
									<div className='h-4 bg-gray-200 rounded w-full'></div>
									<div className='h-4 bg-gray-200 rounded w-3/4'></div>
									<div className='flex items-center space-x-1 mb-2'>
										{[...Array(5)].map((_, starIndex) => (
											<div
												key={starIndex}
												className='h-3 w-3 bg-gray-200 rounded'
											></div>
										))}
										<div className='h-3 bg-gray-200 rounded w-8 ml-1'></div>
									</div>
									<div className='flex items-center space-x-2'>
										<div className='h-5 bg-gray-200 rounded w-16'></div>
										<div className='h-4 bg-gray-200 rounded w-12'></div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</Layout>
	)
}

export default ProductSkeleton
